import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import { SyntaxHighlighter } from 'components/base/SyntaxHighlighter';
import {
  createCurlDataExample,
  updateRecordExample,
  createRecordExample,
  exampleRecordId,
} from 'lib/examples';
import { getRecordUrl } from 'lib/urls';
import { appSection } from 'translations/appSection';

import {
  ApplicationNamePill,
  CodeSectionTitle,
  DocsSection,
  DocsSectionCode,
  DocsSectionText,
} from '../DocSection';
import { ApplicationContext } from './ApplicationContext';

export interface UpdateRecordSectionProps {}

export const UpdateRecordSection: React.FunctionComponent<UpdateRecordSectionProps> = React.memo(
  () => {
    const { application, accountId, apiToken, records } = useContext(ApplicationContext);

    const curlExample = createCurlDataExample(
      'PATCH',
      getRecordUrl(application.id, exampleRecordId(records)),
      updateRecordExample(application),
      accountId,
      apiToken
    );

    return (
      <DocsSection>
        <DocsSectionText>
          <h3 id={`${application.slug}-update`}>
            <FormattedMessage {...appSection.updateRecordTitle} />
          </h3>
          <p>
            <FormattedMessage
              {...appSection.updateRecordText1}
              values={{
                strong: (text: string) => <strong>{text}</strong>,
                code: (children: string) => <ApplicationNamePill>{children}</ApplicationNamePill>,
                applicationName: application.name,
              }}
            />
          </p>
          <p>
            <code>
              {process.env.REACT_APP_API_DOMAIN}/api/v1/applications/<strong>[Table Id]</strong>
              /records/
              <strong>[Record Id]</strong>/
            </code>
          </p>
          <p>
            <FormattedMessage {...appSection.updateRecordText2} />

            <FormattedMessage
              {...appSection.updateRecordText3}
              values={{
                ul: (children: React.ReactNode) => <ul>{children}</ul>,
                li: (children: React.ReactNode) => <li>{children}</li>,
              }}
            />
          </p>

          <p>
            <FormattedMessage
              {...appSection.bulkUpdateRecordText}
              values={{
                link: (children: string) => (
                  <a
                    href="https://developers.smartsuite.com/docs/solution-data/records/bulk-update-records"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {children}
                  </a>
                ),
              }}
            />
          </p>

          <p>
            <FormattedMessage {...appSection.updateRecordText4} />
            <ol>
              <li>
                <FormattedMessage
                  id="update-record-auto-generated-1"
                  defaultMessage="Auto Number"
                />
              </li>
              <li>
                <FormattedMessage id="update-record-auto-generated-2" defaultMessage="Count" />
              </li>
              <li>
                <FormattedMessage
                  id="update-record-auto-generated-3"
                  defaultMessage="First Created"
                />
              </li>
              <li>
                <FormattedMessage id="update-record-auto-generated-4" defaultMessage="Formula" />
              </li>
              <li>
                <FormattedMessage
                  id="update-record-auto-generated-5"
                  defaultMessage="Last Updated"
                />
              </li>
              <li>
                <FormattedMessage id="update-record-auto-generated-6" defaultMessage="Record ID" />
              </li>
              <li>
                <FormattedMessage id="update-record-auto-generated-7" defaultMessage="Rollup" />
              </li>
              <li>
                <FormattedMessage id="update-record-auto-generated-8" defaultMessage="Vote" />
              </li>
            </ol>
          </p>

          <p>
            <FormattedMessage {...appSection.updateRecordText5} />
          </p>
        </DocsSectionText>

        <DocsSectionCode>
          <CodeSectionTitle>
            <FormattedMessage {...appSection.exampleRequest} />
          </CodeSectionTitle>

          <SyntaxHighlighter language="bash">{curlExample}</SyntaxHighlighter>

          <CodeSectionTitle>
            <FormattedMessage {...appSection.exampleResponse} />
          </CodeSectionTitle>

          <SyntaxHighlighter language="json">
            {JSON.stringify(createRecordExample(application), null, 2)}
          </SyntaxHighlighter>
        </DocsSectionCode>
      </DocsSection>
    );
  }
);
